import { Label, Pie, PieChart } from 'recharts'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { useAppSelector } from '@App/hooks'
import { currentWallet } from '@App/redux/slices/user/selectors'
import { InfoIcon } from '@App/static/icons/SpotOrderIcons'
import { ToolTip } from '@App/ui/crypto_exchange/desktop/components/controls/ToolTip'

import { useTrades } from '../../../../model'
import { Card } from '../Card'
import cn from 'clsx'
import s from './styles.module.scss'
import { TradeSentimentBar } from './components/TradeSentimentBar'

const formatNumber = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format

export const Overview = () => {
  const { trades } = useTrades()
  const wallet = useAppSelector(currentWallet)
  const closedTrades = trades.filter((v) => v.status === 'closed')
  const tradesResult = closedTrades.reduce((sum, trade) => sum + trade.result, 0)
  const profitableTradesPercent = (closedTrades.filter((v) => v.result > 0).length * 100) / closedTrades.length
  const sellCount = closedTrades.filter((v) => v.action_type === 'sell').length
  const fundingAdjusment = -11

  return (
    <Card className={s.container} title="Overview" contentClassName={s.content}>
      <div className={s.top}>
        <div className={s.left}>
          <div className={s.title}>
            Win rate
            <ToolTip content={'Profitable trades ÷ total trades'} toolTipClassName={s.toolTip}>
              <InfoIcon className={s.infoIcon} />
            </ToolTip>
          </div>
          <PieChart width={150} height={150}>
            <Pie
              data={[
                { name: 'Win', value: profitableTradesPercent, fill: '#2ab641' },
                { name: 'Lose', value: 100 - profitableTradesPercent, fill: '#606060' },
              ]}
              cx="50%"
              cy="50%"
              outerRadius="80%"
              innerRadius="65%"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none"
            />
            <Label position="center" className={s.chartInnerValue}>
              {`${formatNumber(profitableTradesPercent)} %`}
            </Label>
          </PieChart>
        </div>
        <div className={s.right}>
          <div className={s.row}>
            <div className={s.title}>Profit & Loss</div>
            {
              <div className={cn(s.profitloss, tradesResult < 0 && s.negative)}>
                {getValueWithCurrency(wallet?.currency_code ?? 'USD', tradesResult)}
              </div>
            }
          </div>
          <div className={s.row}>
            <div className={s.title}>Total trades</div>
            <div className={s.totalTrades}>{closedTrades.length}</div>
          </div>
        </div>
      </div>
      <div className={s.bottom}>
        <div className={s.sentimentContainer}>
          <div className={s.title}>Trade sentiment</div>
          <TradeSentimentBar short={sellCount} long={closedTrades.length - sellCount} />
        </div>
        <div className={s.adjustmentContainer}>
          <div className={s.title}>
            Funding adjustments
            <ToolTip
              content={'The total amount paid or received for holding a position overnight'}
              toolTipClassName={s.toolTip}
            >
              <InfoIcon className={s.infoIcon} />
            </ToolTip>
          </div>
          <div className={s.content}>
            <span className={s.title}>Total</span>
            <span className={cn(s.value, fundingAdjusment < 0 && s.negative)}>{fundingAdjusment}$</span>
          </div>
        </div>
      </div>
    </Card>
  )
}
